import { ICartItem } from '@/api/types/cart';
import Button from '@/components/Button';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { EPrescriptionType } from '@/utils/enums/goods';
import { setSession } from '@/utils/tools';
import { DeleteOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React from 'react';

interface InvalidCartItemMobileProps {
  cartItemData: ICartItem;
  batchFlag: boolean;
  // 从useCartItem hooks传来的状态和方法
  isGlassesGrayImage: boolean;
  selected?: boolean;

  // 方法
  getProductImage: () => string;
  getProductName: () => string;
  handleDelete: () => void;
  deleteItem: (cartItem: ICartItem) => Promise<void>;
  // 不可结算商品选中回调
  handleInvalidSelectChange: (checked: boolean) => void;
}

export const InvalidCartItemMobile: React.FC<InvalidCartItemMobileProps> = ({
  cartItemData,
  batchFlag,
  // hooks状态和方法
  isGlassesGrayImage,
  selected = false,

  // hooks方法
  getProductImage,
  getProductName,
  handleDelete,
  deleteItem,
  handleInvalidSelectChange,
}) => {
  const globalT = useTranslations('cart');
  const t = useTranslations('cart.invalidCartItem');
  const router = useRouter();

  const productName = getProductName();
  const productColor = cartItemData.product?.color || '';
  const isGiftItem = cartItemData.type === 4;

  // 判断商品状态
  const getItemStatus = () => {
    const { product, stock, type } = cartItemData;
    if (type === 4) return 'ended';
    if (product?.status === 2) return 'no-restock';
    if (stock < 1) return 'sold-out';
    return null;
  };

  const itemStatus = getItemStatus();
  // 处理商品图片点击
  const handleProductImageClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault(); // 阻止a标签默认跳转

    // 在批量模式下阻止跳转
    if (batchFlag) {
      return;
    }

    if (cartItemData.product?.shop_sku) {
      setSession('zl_goodsDetailEnterPageScene', '购物车页商品条目');
      router.push(`/goods-detail/${cartItemData.product.shop_sku}`);
    }
  };

  // 获取产品图片的className
  const getProductImageClassName = () => {
    let widthClass = '!w-full';
    if (cartItemData.product.is_glass === 1) {
      widthClass = '!w-[82px]'; // 移动端82px
    }
    const blendClass = isGlassesGrayImage ? 'mix-blend-normal' : 'mix-blend-multiply';
    return `${widthClass} object-cover ${blendClass}`;
  };
  // 渲染状态标签
  const renderStatusLabel = () => {
    if (!itemStatus) return null;

    let statusText = '';

    switch (itemStatus) {
      case 'ended':
        statusText = t('ended');
        break;
      case 'no-restock':
        statusText = t('noRestock');
        break;
      case 'sold-out':
        statusText = t('soldOut');
        break;
      default:
        return null;
    }

    return (
      <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
        <div
          className={`flex items-center justify-center w-[60px] h-[60px] rounded-full text-center bg-black/70 text-white text-[10px] whitespace-pre-line`}
        >
          {statusText}
        </div>
      </div>
    );
  };

  // 渲染操作按钮
  const renderActionButton = () => {
    if (batchFlag) {
      return (
        <div className="absolute right-0 bottom-0" onClick={() => deleteItem(cartItemData)}>
          <Button
            className="w-[25px] h-[25px] flex items-center justify-center !bg-[#f8e8e8] !rounded-[6px] !p-0 !text-[13px] !text-[#bc1b1b] !min-w-[25px]"
            sx={{
              border: 'none',
              padding: '0px',
            }}
          >
            <DeleteOutlined className="!text-14" />
          </Button>
        </div>
      );
    }

    // 根据商品和镜片状态决定按钮
    if (cartItemData.product?.status === 2 || cartItemData.stock < 1 || cartItemData.type === 4) {
      return (
        <div
          className="absolute right-0 bottom-0 w-[66px] h-[28px] bg-white border border-gray-400 rounded-[14px] flex items-center justify-center cursor-pointer"
          onClick={handleDelete}
        >
          <span className="text-[12px]">{t('delete')}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`bg-white w-full`}>
      <div className="flex p-0">
        {batchFlag && (
          <div className="pr-[8px] flex items-center justify-center">
            <ZeeloolCheckBox
              checked={selected}
              onChange={(e) => handleInvalidSelectChange(e.target.checked)}
              fontSize={16}
            />
          </div>
        )}
        {/* 商品图片区域 */}
        {/* 商品图片 */}
        <Link
          href={`/goods-detail/${cartItemData.product?.shop_sku}`}
          className={`block relative w-[98px] h-[98px] box-border border border-[#dce1e5] rounded-[8px] z-[1] bg-[#eeeeee] flex items-center justify-center overflow-hidden ${batchFlag ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
          onClick={handleProductImageClick}
        >
          <ProductImageWithLenses
            imgUrl={getProductImage()}
            product={cartItemData.product || {}}
            lens={cartItemData.lens || {}}
            className={getProductImageClassName()}
          />
          {renderStatusLabel()}
          {isGiftItem && (
            <div className="absolute top-[-1px] left-[-1px] bg-[#baacff] h-[15px] leading-[15px] px-[7px] text-[10px] rounded-tl-[8px] rounded-br-[8px] z-8">
              {t('gift')}
            </div>
          )}
        </Link>

        {/* 商品信息区域 */}
        <div className="flex-1 min-w-0 relative ml-[12px]">
          {/* 商品名称 */}
          <div className="mb-[5px]">
            <div className="flex items-start justify-between">
              <span className="text-[14px] font-bb opacity-50">{productName}</span>
            </div>
          </div>

          {/* 商品规格信息 */}
          <div className="space-y-2 text-[12px] opacity-50">
            <div className="mb-[5px]">
              {globalT('GiftsWithCartItem.color')}: {productColor}
            </div>
            {cartItemData.product?.is_glass === 1 &&
              cartItemData.option?.prescription_type &&
              cartItemData.option.prescription_type !== EPrescriptionType.FRAME_ONLY && (
                <div>
                  {cartItemData?.option?.prescription_type_name ||
                    cartItemData.option.prescription_type}
                </div>
              )}
          </div>

          {/* 操作按钮 */}
          {renderActionButton()}
        </div>
      </div>
    </div>
  );
};
